import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App, type AppState } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';

const WORKOUT_REMINDER_NOTIFICATION_ID = 9200;
const REMINDER_DELAY_MS = 60000; // 1 minuto
const STORAGE_KEY_RESUME = 'todayWorkoutResume';
const STORAGE_KEY_EXECUTION_ACTIVE = 'todayWorkoutExecutionActive';

@Injectable({
  providedIn: 'root',
})
export class WorkoutReminderNotificationService {
  private appStateListener: Awaited<ReturnType<(typeof App)['addListener']>> | null = null;

  async init(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      this.appStateListener = await App.addListener('appStateChange', (state: AppState) =>
        this.onAppStateChange(state)
      );
    } catch (e) {
      console.warn('WorkoutReminderNotificationService.init failed', e);
    }
  }

  destroy(): void {
    if (this.appStateListener) {
      this.appStateListener.remove();
      this.appStateListener = null;
    }
  }

  private async onAppStateChange(state: AppState): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      if (state.isActive) {
        await this.cancelReminder();
      } else {
        if (this.hasPendingWorkout()) await this.scheduleReminder();
      }
    } catch (e) {
      console.warn('WorkoutReminderNotificationService onAppStateChange failed', e);
    }
  }

  private hasPendingWorkout(): boolean {
    try {
      if (localStorage.getItem(STORAGE_KEY_EXECUTION_ACTIVE) === 'true') return true;
      const raw = localStorage.getItem(STORAGE_KEY_RESUME);
      if (!raw) return false;
      const data = JSON.parse(raw) as { workoutId?: string; sessionId?: string };
      return !!(data?.workoutId && data?.sessionId);
    } catch {
      return false;
    }
  }

  private async scheduleReminder(): Promise<void> {
    try {
      const status = await LocalNotifications.requestPermissions();
      if (status.display !== 'granted') return;
      await LocalNotifications.cancel({ notifications: [{ id: WORKOUT_REMINDER_NOTIFICATION_ID }] });
      await LocalNotifications.schedule({
        notifications: [
          {
            id: WORKOUT_REMINDER_NOTIFICATION_ID,
            title: 'Treino em execução',
            body: 'Você tem um treino pendente. Toque para continuar.',
            schedule: { at: new Date(Date.now() + REMINDER_DELAY_MS) },
          },
        ],
      });
    } catch (e) {
      console.warn('WorkoutReminderNotificationService.scheduleReminder failed', e);
    }
  }

  private async cancelReminder(): Promise<void> {
    try {
      await LocalNotifications.cancel({ notifications: [{ id: WORKOUT_REMINDER_NOTIFICATION_ID }] });
    } catch (e) {
      console.warn('WorkoutReminderNotificationService.cancelReminder failed', e);
    }
  }
}
